import { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { unwrapResult } from '@reduxjs/toolkit';
import isEmpty from 'lodash/isEmpty';
import RouteWithPageTitle, { RouteWithPageTitleProps } from './RouteWithPageTitle';
import { useSignIn } from '../auth/hooks/useSignIn';
import { fromAccounts, fromAuth, fromOrganizations, useAppDispatch } from '../store';
import { useAuthenticate } from './useAuthenticate';
import { useLaunchDarkly } from '../launchdarkly';
import { useAccessToken } from '../auth/providers/AccessTokenContext';
import { useConfig } from '../config';
import { OIDC_REDIRECT_URI_KEY, setRedirectUri } from '../utils/redirect-uri';
import { useQuery } from './useQuery';

const ProtectedRoute = (props: RouteWithPageTitleProps) => {
  const { initialized, flags } = useLaunchDarkly();

  if (!initialized) {
    // If LaunchDarkly is not initialized, we should not render the protected route
    return <></>;
  }

  if (flags?.loginV2Enabled) {
    return <ProtectedRouteV2 {...props} />;
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useAuthenticate() ? <RouteWithPageTitle {...props} /> : <></>;
};

const ProtectedRouteV2 = (props: RouteWithPageTitleProps) => {
  const accessToken = useAccessToken();
  const { signInWithPath } = useSignIn();

  const dispatch = useAppDispatch();
  const location = useLocation();
  const { get } = useQuery();

  const orgId = get('orgId');
  const organization = useSelector(fromOrganizations.selectOrganizationById(Number(orgId)));

  const accountId = get('accountId');
  const account = useSelector(fromAccounts.selectAccountById(Number(accountId)));

  const user = useSelector(fromAuth.selectUser);

  const [validContext, setValidContext] = useState<'NOT_CHECK' | 'VALID' | 'INVALID'>(
    'NOT_CHECK',
  );
  const [fetchedContext, setFetchedContext] = useState(false);

  const { config } = useConfig();

  const auth = useAuth();

  useEffect(() => {
    if (accessToken.status === 'idle') {
      signInWithPath?.(location ?? undefined, accountId, orgId);
    }
    if (accessToken.status === 'signedIn') {
      dispatch(fromAuth.doPostSignIn({ jwt: accessToken.jwt }));
      const accIdNumber = Number(accountId);
      const orgIdNumber = Number(orgId);
      if (Number.isInteger(accIdNumber) && accIdNumber) {
        dispatch(fromAccounts.doGetAccountById({ id: accIdNumber }))
          .then(unwrapResult).finally(() => setFetchedContext(true));
      } else if (Number.isInteger(orgIdNumber) && orgIdNumber) {
        dispatch(fromOrganizations.doFetchOrganizationById({ id: orgIdNumber }))
          .then(unwrapResult).finally(() => setFetchedContext(true));
      }
    }
  }, [accessToken, signInWithPath]);

  useEffect(() => {
    setFetchedContext(false);
    if (!accountId && !orgId) {
      setValidContext('VALID');
    } else {
      setValidContext('NOT_CHECK');
    }
  }, [accountId, orgId]);

  useEffect(() => {
    if (!fetchedContext || !accessToken.accountUuid) return;
    if (account) {
      if (account.uuid === accessToken.accountUuid) {
        setValidContext('VALID');
      } else {
        setValidContext('INVALID');
      }
    } else if (organization) {
      // @ts-expect-error
      if (organization.accountUuid === accessToken.accountUuid) {
        setValidContext('VALID');
      } else {
        setValidContext('INVALID');
      }
    } else if (accountId || orgId) {
      setValidContext('INVALID');
    }
  }, [JSON.stringify(account), JSON.stringify(organization), accessToken, fetchedContext]);

  useEffect(() => {
    if (!config || !location || !fetchedContext) return;
    if (validContext !== 'INVALID') return;
    sessionStorage.removeItem(`oidc.user:${config.keyCloakUrl}/realms/${config.keyCloakRealmId}:katalon-one`);
    const extraQueryParams: Record<string, string | number> = {};
    const aid = account?.id ?? organization?.accountId ?? '';
    if (aid) {
      extraQueryParams.aid = aid;
    }
    const oid = organization?.id ?? '';
    if (oid) {
      extraQueryParams.oid = oid;
    }
    if (isEmpty(extraQueryParams)) {
      // when invalid both account and organization
      extraQueryParams.k_prompt = 'account';
    } else {
      // only set redirect uri when data is present
      setRedirectUri(location);
    }
    auth.signinRedirect?.({
      extraQueryParams,
    });
  }, [config, validContext, location]);

  useEffect(() => {
    if (validContext === 'VALID') {
      sessionStorage.removeItem(OIDC_REDIRECT_URI_KEY);
    }
  }, [validContext]);

  if (accessToken.status !== 'signedIn' || !user || validContext !== 'VALID') {
    return <></>;
  }

  return <RouteWithPageTitle {...props} />;
};

export default ProtectedRoute;
